import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  Stethoscope,
  Timer,
  CurrencyDollar,
  Globe,
  Warning,
} from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import { useToast } from "../../contexts/ToastContext";
import {
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from "../../store/services/tasksApi";
import {
  selectTherapistId,
  selectTherapistName,
} from "../../store/slices/authSlice";
import {
  normalizeTreatmentType,
  getPublicTypeWarnings,
  DURATION_PRESETS,
  CATEGORY_COLORS,
} from "../../utils/treatmentTypeRules";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./TreatmentTypeFormModal.css";

/**
 * Create / edit a treatment type (clinics/{id}/categories, type "treatmentType").
 * ONE list serves both the internal appointment wizard and the public
 * self-booking page, so this modal is shared by Clinic Settings and the
 * Calendar Settings booking panel.
 *
 * Props:
 *  - clinicId
 *  - editingType: existing category doc, or null to create
 *  - onClose()
 *  - onSaved?(category)
 */
const TreatmentTypeFormModal = ({ clinicId, editingType = null, onClose, onSaved }) => {
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const { showSuccess, showError } = useToast();
  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
  const isSaving = isCreating || isUpdating;
  const isEditing = !!editingType;

  const [form, setForm] = useState(() => {
    const t = editingType ? normalizeTreatmentType(editingType) : null;
    return {
      name: t?.name || "",
      color: t?.color || CATEGORY_COLORS[0],
      rate: t?.rate != null ? String(t.rate) : "",
      duration: t?.duration != null ? String(t.duration) : "",
      isPublic: t?.isPublic ?? false,
      showPrice: t?.showPrice ?? false,
      showDuration: t?.showDuration ?? true,
    };
  });

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const warnings = useMemo(
    () =>
      getPublicTypeWarnings({
        rate: form.rate,
        duration: form.duration,
        isPublic: form.isPublic,
        showPrice: form.showPrice,
        showDuration: form.showDuration,
      }),
    [form.rate, form.duration, form.isPublic, form.showPrice, form.showDuration],
  );

  const durationNum = parseInt(form.duration, 10);
  const durationInvalid = form.duration !== "" && (!durationNum || durationNum < 1);
  const rateNum = parseFloat(form.rate);
  const rateInvalid = form.rate !== "" && (Number.isNaN(rateNum) || rateNum < 0);
  const canSave = form.name.trim() && !durationInvalid && !rateInvalid && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    const categoryData = {
      name: form.name.trim(),
      color: form.color,
      rate: form.rate !== "" ? Number(form.rate) : null,
      duration: form.duration !== "" ? Number(form.duration) : null,
      isPublic: form.isPublic === true,
      showPrice: form.showPrice === true,
      showDuration: form.showDuration !== false,
    };
    try {
      let saved;
      if (isEditing) {
        saved = await updateCategory({
          clinicId,
          categoryId: editingType.id,
          categoryData,
          therapistId,
          therapistName,
        }).unwrap();
      } else {
        saved = await createCategory({
          clinicId,
          categoryData: { ...categoryData, type: "treatmentType" },
          therapistId,
          therapistName,
        }).unwrap();
      }
      showSuccess(isEditing ? "סוג הטיפול עודכן בהצלחה" : "סוג הטיפול נוצר בהצלחה");
      onSaved?.(saved);
      onClose();
    } catch (error) {
      console.error("[CK-3014] Error saving treatment type:", {
        errorCode: "CK-3014",
        errorMessage: error.message,
        clinicId,
        categoryId: editingType?.id,
        isEditing,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת סוג הטיפול");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    }
  };

  // Portaled to <body>, but React still bubbles synthetic events through the
  // component tree — stop them so a click here never reaches a parent
  // overlay's onClose (e.g. the calendar-settings modal).
  const handleOverlayClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  return createPortal(
    <div className="ttf-overlay" onClick={handleOverlayClick} onKeyDown={handleKeyDown}>
      <div
        className="ttf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ttf-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ttf-header">
          <div className="ttf-title">
            <div className="ttf-title-icon">
              <Stethoscope size={20} weight="fill" />
            </div>
            <div className="ttf-title-text">
              <span className="ttf-eyebrow">סוג טיפול</span>
              <h3 id="ttf-title">{isEditing ? "עריכת סוג טיפול" : "סוג טיפול חדש"}</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="ttf-body">
          <div className="ttf-field">
            <label className="ttf-label required" htmlFor="ttf-name">
              שם סוג הטיפול
            </label>
            <input
              id="ttf-name"
              type="text"
              className="ttf-input"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="לדוגמה: טיפול ראשוני, ייעוץ, אבחון..."
              maxLength={60}
              autoFocus
            />
          </div>

          <div className="ttf-field">
            <span className="ttf-label">צבע</span>
            <div className="ttf-colors" role="radiogroup" aria-label="צבע">
              {CATEGORY_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  role="radio"
                  aria-checked={form.color === color}
                  className={`ttf-color ${form.color === color ? "selected" : ""}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setField("color", color)}
                />
              ))}
            </div>
          </div>

          <div className="ttf-grid">
            <div className="ttf-field">
              <label className="ttf-label" htmlFor="ttf-rate">
                <CurrencyDollar size={14} /> תעריף (₪)
              </label>
              <input
                id="ttf-rate"
                type="number"
                className={`ttf-input ttf-input-ltr ${rateInvalid ? "invalid" : ""}`}
                value={form.rate}
                onChange={(e) => setField("rate", e.target.value)}
                placeholder="לא חובה"
                min="0"
                inputMode="decimal"
              />
              {rateInvalid && <span className="ttf-error">תעריף לא תקין</span>}
            </div>
            <div className="ttf-field">
              <label className="ttf-label" htmlFor="ttf-duration">
                <Timer size={14} /> משך (דקות)
              </label>
              <input
                id="ttf-duration"
                type="number"
                className={`ttf-input ttf-input-ltr ${durationInvalid ? "invalid" : ""}`}
                value={form.duration}
                onChange={(e) => setField("duration", e.target.value)}
                placeholder="לא חובה"
                min="1"
                step="1"
                inputMode="numeric"
              />
              {durationInvalid && <span className="ttf-error">משך מינימלי דקה אחת</span>}
            </div>
          </div>

          <div className="ttf-presets" aria-label="משכים נפוצים">
            {DURATION_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`ttf-preset ${durationNum === preset ? "active" : ""}`}
                onClick={() => setField("duration", String(preset))}
              >
                {preset} דק׳
              </button>
            ))}
          </div>
          <span className="ttf-hint">
            תעריף ומשך שמוגדרים כאן ימולאו אוטומטית בקביעת פגישה מסוג זה (ניתן לשנות ידנית).
          </span>

          <div className={`ttf-public ${form.isPublic ? "on" : ""}`}>
            <button
              type="button"
              className="ttf-toggle-row"
              onClick={() => setField("isPublic", !form.isPublic)}
              aria-pressed={form.isPublic}
            >
              <div className="ttf-toggle-text">
                <span className="ttf-toggle-title">
                  <Globe size={15} weight={form.isPublic ? "fill" : "regular"} />
                  הצג בעמוד ההזמנה העצמית
                </span>
                <span className="ttf-toggle-sub">
                  מטופלים יוכלו לבחור שירות זה בקישור ההזמנה; המועדים יחושבו לפי המשך.
                </span>
              </div>
              <span className={`ttf-switch ${form.isPublic ? "active" : ""}`}>
                <span className="ttf-switch-knob" />
              </span>
            </button>

            {form.isPublic && (
              <div className="ttf-sub-toggles">
                <button
                  type="button"
                  className="ttf-sub-toggle"
                  onClick={() => setField("showDuration", !form.showDuration)}
                  aria-pressed={form.showDuration}
                >
                  <span>הצג משך למטופל</span>
                  <span className={`ttf-switch small ${form.showDuration ? "active" : ""}`}>
                    <span className="ttf-switch-knob" />
                  </span>
                </button>
                <button
                  type="button"
                  className="ttf-sub-toggle"
                  onClick={() => setField("showPrice", !form.showPrice)}
                  aria-pressed={form.showPrice}
                >
                  <span>הצג מחיר למטופל</span>
                  <span className={`ttf-switch small ${form.showPrice ? "active" : ""}`}>
                    <span className="ttf-switch-knob" />
                  </span>
                </button>
              </div>
            )}

            {warnings.length > 0 && (
              <div className="ttf-warnings" role="alert">
                {warnings.map((w) => (
                  <div key={w} className="ttf-warning">
                    <Warning size={14} weight="fill" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ttf-preview">
            <span className="ttf-preview-label">תצוגה מקדימה:</span>
            <span
              className="ttf-preview-badge"
              style={{ backgroundColor: form.color + "20", color: form.color }}
            >
              <span className="ttf-preview-dot" style={{ backgroundColor: form.color }} />
              {form.name || "שם סוג הטיפול"}
            </span>
            {form.duration && !durationInvalid && (
              <span className="ttf-preview-meta">
                <Timer size={12} /> {form.duration} דק׳
              </span>
            )}
            {form.rate && !rateInvalid && (
              <span className="ttf-preview-meta ttf-preview-rate">₪{form.rate}</span>
            )}
          </div>
        </div>

        <div className="ttf-footer">
          <button
            type="button"
            className="ttf-btn ttf-btn-secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            ביטול
          </button>
          <button
            type="button"
            className="ttf-btn ttf-btn-primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving && <LoaderLoop size="xs" />}
            <span>{isEditing ? "שמור שינויים" : "צור סוג טיפול"}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TreatmentTypeFormModal;
